import { Theme } from "@mui/material";
import type { ReactNode } from "react";
import type {
  CountryBreakdown,
  HoldingBreakdown,
  IndustryBreakdownV2,
} from "@rentablo/api/apiv1";
import type { ItemGroup } from "./components/BreakdownDetails";

const getColorKey = (index: number) => {
  const colorIndex = index % 14;
  const colorKey =
    colorIndex < 9 ? `0${colorIndex + 1}` : (colorIndex + 1).toString();
  return colorKey;
};

interface GetBreakdownDetailsParams {
  breakdown: CountryBreakdown[] | IndustryBreakdownV2[];
  investmentBreakdown: CountryBreakdown[] | IndustryBreakdownV2[];
  logos: {
    recommendation?: ReactNode;
    originalFund?: ReactNode;
  };
  isGerman?: boolean;
}

export const getBreakdownDetails = ({
  breakdown,
  investmentBreakdown,
  logos,
  isGerman,
}: GetBreakdownDetailsParams): ItemGroup[] => {
  const groups = new Map<string, ItemGroup>();

  const addEntries = (
    entries: (CountryBreakdown | IndustryBreakdownV2)[] | undefined,
    key: "recommendation" | "originalFund"
  ) => {
    (entries ?? []).forEach(({ reference, percent }) => {
      const name = (isGerman ? reference.germanName : reference.name) || "";
      if (!groups.has(name)) {
        groups.set(name, {
          name: name,
          items: [
            { logo: logos.recommendation, value: 0 },
            { logo: logos.originalFund, value: 0 },
          ],
        });
      }

      const group = groups.get(name)!;
      const index = key === "recommendation" ? 0 : 1;
      group.items[index] = {
        logo:
          key === "recommendation" ? logos.recommendation : logos.originalFund,
        value: percent,
      };
    });
  };

  addEntries(breakdown, "recommendation");
  addEntries(investmentBreakdown, "originalFund");

  return Array.from(groups.values()).sort(
    (a, b) =>
      Math.max(...b.items.map((i) => i.value)) -
      Math.max(...a.items.map((i) => i.value))
  );
};

export const getBreakdownChartData = (
  data: CountryBreakdown[] | IndustryBreakdownV2[],
  theme: Theme,
  isGerman?: boolean
) =>
  data
    .sort((a, b) => b.percent - a.percent)
    .map((item, index) => ({
      name: isGerman
        ? (item.reference?.germanName ?? "")
        : (item.reference?.name ?? ""),
      id: item.reference?.id?.toString() ?? item.reference?.name ?? "",
      value: item.percent,
      y: item.percent,
      color: theme.palette.graphVisual[getColorKey(index)]?.mid,
    }));

export const getHoldingsChartData = (data: HoldingBreakdown[], theme: Theme) =>
  data
    .sort((a, b) => b.percent - a.percent)
    .map((item, index) => ({
      name: item.holdingName ?? "",
      id: item.holdingName ?? "",
      value: item.percent,
      y: item.percent,
      color: theme.palette.graphVisual[getColorKey(index)]?.mid,
    }));
